import React, { useContext, useRef, useCallback, useState, useEffect } from 'react';
import { MapContainer, Marker, LayersControl, WMSTileLayer, useMapEvents } from 'react-leaflet';
import { resetGlobalContext } from '../../utils/contextResetUtils';
import { usePropertyFilters } from '../../contexts/PropertyFilterContext';
import { buscarPorCodigo } from '../../services/PropertyService';
import { logPropertyView } from '../../services/ActivityLogService';
import { getUserData } from '../../services/AuthService';
import 'leaflet/dist/leaflet.css';
import Paper from '@mui/material/Paper';
import UnifiedSearchBar from '../SearchBar/UnifiedSearchBar';
import { MapContext } from '../../contexts/MapContext';
import * as L from 'leaflet';
import ResetZoomButton from './components/ResetZoomButton';
import ZoomLevelControl from './components/ZoomLevelControl';
import Box from '@mui/material/Box';
import BaseLayersControl from './BaseLayersControl';
import ThematicLayersControl from './ThematicLayersControl';
import TailwindLayerStyleConfig from './components/TailwindLayerStyleConfig';
import LayerStyleConfigButton from './components/LayerStyleConfigButton';
import ProcessingOverlay from './ProcessingOverlay';

import MunicipalityLayer from './MunicipalityLayer';
import StateBoundariesLayer from './StateBoundariesLayer';
import FitBoundsHandler from './FitBoundsHandler';
// Import do PropertyView em vez do Sidebar
import PropertyView from '../Property/PropertyView';
import VectorTileLayers from './VectorTileLayers';
// Import do ErrorBoundary para tratamento de erros
import ErrorBoundary from '../Property/ErrorBoundary';
import useWmsLayers from '../../hooks/useWmsLayers';
import usePropertyHandler from '../../hooks/usePropertyHandler';
import useLayerStyleConfig from '../../hooks/useLayerStyleConfig';
import useMapAnimationFix from '../../hooks/useMapAnimationFix';
import useMapClick from '../../hooks/useMapClick';
import useResetOnZoomReset from '../../hooks/useResetOnZoomReset';
import useAuth from '../../hooks/useAuth';
import { getVectorLayersConfig } from '../../config/vector-layers';

// Configura ícones default do Leaflet para Vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
});

// Instância do ícone default (azul)
const defaultIcon = new L.Icon.Default();

// Marcador vermelho (não encontrado)
const redIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Marcador amarelo (processando)
const yellowIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png',
  iconRetinaUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const INITIAL_CENTER = [-15.8, -47.9];
const INITIAL_ZOOM = 5;
const LAYER_STYLES_STORAGE_KEY = 'userLayerStyles';
const normalizeLayerStyleKey = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const layerStylesDefault = {
  Biomas: {
    strokeColor: '#2E7D32',
    strokeOpacity: 0.9,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#A5D6A7',
    fillOpacity: 0.35,
  },
  'ÁREA DO IMÓVEL': {
    strokeColor: '#00bfff',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'solid',
    fillColor: '#b3e5fc',
    fillOpacity: 0.2,
  },
  'RESERVA LEGAL': {
    strokeColor: '#228B22',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'solid',
    fillColor: '#a5d6a7',
    fillOpacity: 0.18,
  },
  APP: {
    strokeColor: '#388e3c',
    strokeOpacity: 0.9,
    strokeWeight: 2,
    strokeDash: 'dot',
    fillColor: '#c8e6c9',
    fillOpacity: 0.16,
  },
  'VEGETAÇÃO NATIVA': {
    strokeColor: '#689f38',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'solid',
    fillColor: '#dcedc8',
    fillOpacity: 0.15,
  },
  HIDROGRAFIA: {
    strokeColor: '#1976d2',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'dash',
    fillColor: '#bbdefb',
    fillOpacity: 0.13,
  },
  'ÁREA CONSOLIDADA': {
    strokeColor: '#ffb300',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'solid',
    fillColor: '#ffe082',
    fillOpacity: 0.14,
  },
  'ÁREA DE SERVIDÃO': {
    strokeColor: '#8d6e63',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'dash',
    fillColor: '#d7ccc8',
    fillOpacity: 0.12,
  },
  'ÁREA DE POUSIO': {
    strokeColor: '#fbc02d',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'dot',
    fillColor: '#fff9c4',
    fillOpacity: 0.11,
  },
  'USO RESTRITO': {
    strokeColor: '#6d4c41',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'dash',
    fillColor: '#d7ccc8',
    fillOpacity: 0.11,
  },
  SIGEF: {
    strokeColor: '#e53935',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDash: 'solid',
    fillColor: '#ffcdd2',
    fillOpacity: 0.16,
  },
  Uc: {
    strokeColor: '#1565C0',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#90CAF9',
    fillOpacity: 0.35,
  },
  Aldeias: {
    strokeColor: '#6D4C41',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#BCAAA4',
    fillOpacity: 0.95,
    radius: 5,
  },
  'Auto Infracao Icmbio': {
    strokeColor: '#BF360C',
    strokeOpacity: 1,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#FFB74D',
    fillOpacity: 0.4,
    radius: 5,
  },
  Assentamentos: {
    strokeColor: '#4E342E',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#D7CCC8',
    fillOpacity: 0.35,
  },
  'Embargos Ibama': {
    strokeColor: '#B71C1C',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#EF9A9A',
    fillOpacity: 0.35,
  },
  'Embargos Icmbio': {
    strokeColor: '#7F0000',
    strokeOpacity: 1,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#EF9A9A',
    fillOpacity: 0.35,
    radius: 5,
  },
  Florestas: {
    strokeColor: '#1B5E20',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#C8E6C9',
    fillOpacity: 0.35,
  },
  Mineracao: {
    strokeColor: '#FF8F00',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#FFE0B2',
    fillOpacity: 0.35,
  },
  Quilombolas: {
    strokeColor: '#4A148C',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#E1BEE7',
    fillOpacity: 0.35,
  },
  'Terras Indigenas': {
    strokeColor: '#BF360C',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#FFCCBC',
    fillOpacity: 0.35,
  },
  Pra: {
    strokeColor: '#00695C',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    strokeDash: 'solid',
    fillColor: '#80CBC4',
    fillOpacity: 0.35,
  },
  'Curso Dagua': {
    strokeColor: '#0D47A1',
    strokeOpacity: 0.85,
    strokeWeight: 1.2,
    strokeDash: 'solid',
    fillColor: '#BBDEFB',
    fillOpacity: 0.25,
  },
  'Curso Dagua Trecho': {
    strokeColor: '#1976D2',
    strokeOpacity: 0.85,
    strokeWeight: 1.4,
    strokeDash: 'dash',
    fillColor: '#90CAF9',
    fillOpacity: 0.18,
  },
  'Massa Dagua': {
    strokeColor: '#01579B',
    strokeOpacity: 0.85,
    strokeWeight: 1.1,
    strokeDash: 'solid',
    fillColor: '#4FC3F7',
    fillOpacity: 0.32,
  },
  Rios: {
    strokeColor: '#0288D1',
    strokeOpacity: 0.85,
    strokeWeight: 1.3,
    strokeDash: 'solid',
    fillColor: '#B3E5FC',
    fillOpacity: 0.2,
  },
};

function SimpleMapView() {
  const mapContext = useContext(MapContext);
  const {
    sidebarOpen,
    setSidebarOpen,
    marker,
    processStatus,
    retryProcessingSearch,
    themeGeometries,
    estados,
    propertyData,
    setPropertyData,
    propertyCodes,
    setPropertyCodes,
  } = mapContext;
  const filterContext = usePropertyFilters();
  const mapRef = useRef();
  const boxRef = useRef();
  const [zoomLevel, setZoomLevel] = useState(INITIAL_ZOOM);
  const [bounds, setBounds] = useState(null);
  const [_loading, setLoading] = useState(false);
  const [sidebarData, setSidebarData] = useState(null);
  const wmsLayers = useWmsLayers();
  usePropertyHandler();
  const {
    layerStyles,
    pendingStyles,
    open,
    setOpen,
    handlePendingChange,
    handleReset,
    handleSave,
  } = useLayerStyleConfig(LAYER_STYLES_STORAGE_KEY, layerStylesDefault);
  const forceFitBounds = useMapAnimationFix(mapRef, boxRef);
  const { user } = useAuth();
  const handleOpenLayerStyle = useCallback(() => setOpen(true), [setOpen]);
  const styleConfigLayerNames = React.useMemo(() => {
    const vectorLayers = getVectorLayersConfig();
    const vectorStyleKeys = vectorLayers
      .map((layer) => layer.styleKey || layer.name)
      .filter(Boolean);
    const vectorAliases = new Set(
      vectorLayers
        .flatMap((layer) => [layer.styleKey, layer.name])
        .filter(Boolean)
        .map((name) => normalizeLayerStyleKey(name)),
    );

    const nonVectorNames = Object.keys(pendingStyles || {}).filter(
      (name) => !vectorAliases.has(normalizeLayerStyleKey(name)),
    );

    const merged = [...nonVectorNames, ...vectorStyleKeys];
    return Array.from(new Map(merged.map((name) => [normalizeLayerStyleKey(name), name])).values());
  }, [pendingStyles]);

  // === Cálculo do filename do marker ===
  // Derivação robusta do markerFilename
  let markerFilename = null;
  if (marker?.properties?.filename) markerFilename = marker.properties.filename;
  else if (marker?.filename) markerFilename = marker.filename;
  else if (marker?.data?._metadata?.file_id)
    markerFilename = `${marker.data._metadata.file_id}.json`;
  else if (marker?.data?.sigef && marker.data.sigef[0]?.codigo_imo)
    markerFilename = `${marker.data.sigef[0].codigo_imo}.json`;
  else if (marker?.data?.car && marker.data.car[0]?.cod_imovel)
    markerFilename = `${marker.data.car[0].cod_imovel}.json`;

  // Atualiza o contexto propertyCodes se encontrar um filename novo
  useEffect(() => {
    if (markerFilename && setPropertyCodes && propertyCodes?.filename !== markerFilename) {
      setPropertyCodes({ ...propertyCodes, filename: markerFilename });
    }
  }, [markerFilename, setPropertyCodes, propertyCodes]);

  const showRed = marker?.found === false;
  const showYellow = marker?.processing === true;

  // Função para registrar visualização da propriedade
  const registerPropertyView = async (propertyDataToLog) => {
    try {
      // Verificar se temos dados do usuário no contexto, se não, obter
      let currentUserId = user?.id;

      if (!currentUserId) {
        try {
          const userData = await getUserData();
          if (userData && userData.id) {
            currentUserId = userData.id;
          }
        } catch (error) {
          console.error('Erro ao obter ID do usuário para log de visualização:', error);
        }
      }

      // Se temos o ID do usuário e dados da propriedade, registrar a visualização
      if (currentUserId && propertyDataToLog) {
        await logPropertyView(currentUserId, propertyDataToLog);
      }
    } catch (error) {
      // Log apenas - não interrompe o fluxo principal
      console.error('Erro ao registrar visualização de propriedade:', error);
    }
  };

  const handleMarkerClick = useCallback(
    (e, markerData) => {
      // Abre a sidebar
      setSidebarOpen(true);

      // Dados a serem usados para log (propriedade em contexto ou dados do marcador)
      const _dataToLog = propertyData || markerData?.data;

      // Verificar todas as possíveis origens do filename
      let filename = null;

      // 1. Verificar no contexto global
      if (propertyCodes?.filename) {
        filename = propertyCodes.filename;
        //console.log(`[SimpleMapView] Usando filename do contexto global: ${filename}`);
      }
      // 2. Verificar nos metadados da propriedade global
      else if (propertyData?._metadata?.file_id) {
        filename = `${propertyData._metadata.file_id}.json`;
        //console.log(`[SimpleMapView] Usando filename dos metadados da propriedade: ${filename}`);
      }
      // 3. Verificar no próprio markerData.properties (adicionado via processing.js)
      else if (markerData?.properties?.filename) {
        filename = markerData.properties.filename;
        //console.log(`[SimpleMapView] Usando filename do markerData.properties: ${filename}`);
      }
      // 4. NOVA FONTE: Verificar se o markerData.data contém _metadata (visto nos logs)
      else if (markerData?.data?._metadata?.file_id) {
        filename = `${markerData.data._metadata.file_id}.json`;
        //console.log(`[SimpleMapView] Usando file_id dos metadados do markerData: ${filename}`);
      }
      // 5. FALLBACK TEMPORÁRIO - Usar markerData.data (visto em logs recentes)
      else if (markerData?.data?.car || markerData?.data?.sigef) {
        //console.log('[SimpleMapView] Tentando extrair códigos do markerData.data');

        // Extrai primeiro dados do markerData.data que vimos nos logs
        const codigo_imo =
          // Do markerData.data.sigef
          (markerData?.data?.sigef && markerData.data.sigef[0]?.codigo_imo) ||
          (markerData?.data?.sigef &&
            markerData.data.sigef[0]?.parcels &&
            markerData.data.sigef[0].parcels[0]?.codigo_imo) ||
          // Fallbacks anteriores
          markerData?.properties?.codigo_imo ||
          propertyData?.sigef?.codigo_imo ||
          (propertyData?.sigef?.parcels && propertyData.sigef.parcels[0]?.codigo_imo);

        const cod_imovel =
          // Do markerData.data.car
          (markerData?.data?.car && markerData.data.car[0]?.cod_imovel) ||
          // Fallbacks anteriores
          markerData?.properties?.cod_imovel ||
          markerData?.properties?.codigo_imovel ||
          propertyData?.car?.cod_imovel ||
          (propertyData?.car?.themes && propertyData.car.themes[0]?.cod_imovel);

        // Gerar filename usando mesma lógica do backend
        if (codigo_imo) {
          filename = `${codigo_imo}.json`;
          //console.log(`[SimpleMapView] Gerado filename do código SIGEF no markerData: ${filename}`);
        } else if (cod_imovel) {
          filename = `${cod_imovel}.json`;
          //console.log(`[SimpleMapView] Gerado filename do código CAR no markerData: ${filename}`);
        } else {
          //console.log('[SimpleMapView] Impossível gerar filename do markerData: estrutura inesperada');
        }

        // Se gerou filename, atualiza o contexto para uso futuro
        if (filename && setPropertyCodes) {
          const updatedCodes = { ...propertyCodes, filename };
          setPropertyCodes(updatedCodes);
          //console.log(`[SimpleMapView] Contexto atualizado com filename: ${filename}`);
        }
      }
      // 6. Último recurso - outros campos do markerData ou propertyData
      else {
        //console.log('[SimpleMapView] Nenhum metadado ou código utilizável no markerData');
      }

      // Em DEV/PROD não usamos mais /json. Se já temos propertyData DO MESMO file_id, exibe na sidebar; caso contrário, dispara /search.
      const markerFileId = filename ? String(filename).replace(/\.json$/, '') : null;
      const sameFile = Boolean(
        propertyData &&
        markerFileId &&
        ((propertyData?._metadata?.file_id && propertyData._metadata.file_id === markerFileId) ||
          (propertyData?._contextData?.filename &&
            String(propertyData._contextData.filename).replace(/\.json$/, '') === markerFileId)),
      );
      if (propertyData && sameFile) {
        setSidebarData(propertyData);

        // Registrar visualização da propriedade
        registerPropertyView(propertyData);
        return;
      }
      const DEBUG = import.meta.env.VITE_DEBUG === 'true';
      try {
        // 1) hints do contexto atual
        const hints = propertyData?._contextData?.hints || {};
        // 2) códigos no markerData
        const cod_imovel_marker =
          (markerData?.data?.car && markerData.data.car[0]?.cod_imovel) ||
          markerData?.properties?.cod_imovel ||
          null;
        const codigo_imo_marker =
          (markerData?.data?.sigef && markerData.data.sigef[0]?.codigo_imo) ||
          markerData?.properties?.codigo_imo ||
          null;
        // 3) derivação a partir do filename (CAR tem hífen; SIGEF 13 dígitos)
        let cod_imovel_from_filename = null;
        let codigo_imo_from_filename = null;
        if (filename) {
          const fname = filename.replace(/\.json$/, '');
          if (/^\d{13}$/.test(fname)) codigo_imo_from_filename = fname;
          if (/^[A-Z]{2}-\d{7}/i.test(fname)) cod_imovel_from_filename = fname;
        }
        const params = {};
        const cod_imovel = hints.cod_imovel || cod_imovel_marker || cod_imovel_from_filename;
        const codigo_imo = hints.codigo_imo || codigo_imo_marker || codigo_imo_from_filename;
        if (cod_imovel) params.cod_imovel = cod_imovel;
        if (codigo_imo) params.codigo_imo = codigo_imo;
        // eslint-disable-next-line no-console
        if (DEBUG) console.log('[SimpleMapView] click -> /search params:', params);
        if (Object.keys(params).length > 0) {
          setLoading(true);
          buscarPorCodigo(params, setPropertyData, setLoading)
            .then((result) => {
              // Registrar visualização após receber os dados da propriedade
              if (result) {
                registerPropertyView(result);
              }
            })
            .catch((e) => {
              if (DEBUG) console.warn('[SimpleMapView] buscarPorCodigo click error:', e);
            })
            .finally(() => setLoading(false));
        } else if (markerData?.data) {
          // Se não temos parâmetros mas temos dados no marcador, registrar visualização com esses dados
          registerPropertyView(markerData.data);
        }
      } catch (_) {
        /* no-op */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSidebarOpen, propertyCodes, propertyData, setSidebarData, setLoading],
  );

  useEffect(() => {
    /* console.log('marker:', marker); */
  }, [marker]);

  useEffect(() => {
    // console.log('sidebarData:', sidebarData);
  }, [sidebarData]);

  useEffect(() => {
    // console.log('zoomLevel:', zoomLevel);
  }, [zoomLevel]);

  useEffect(() => {
    if (propertyCodes.filename) {
      // DESABILITADO TEMPORARIAMENTE - Leiturado JSON em disco causava erro de sintaxe
      //console.log('Leitura de JSON em disco desabilitada temporariamente:', propertyCodes.filename);
      // Usar dados da propriedade que já temos em memória (propertyData do context)
      if (propertyData) {
        setSidebarData(propertyData);
      } else {
        setSidebarData(null);
      }
    } else {
      setSidebarData(null);
    }
  }, [propertyCodes.filename, propertyData]);

  useResetOnZoomReset();

  const getStyleForTheme = useCallback(
    (tema) => {
      const normalizedTema = normalizeLayerStyleKey(tema);
      const resolvedKey = Object.keys(layerStyles).find(
        (key) => normalizeLayerStyleKey(key) === normalizedTema,
      );
      const style = resolvedKey ? layerStyles[resolvedKey] : layerStyles[tema];
      let dashArray;
      if (style) {
        if (style.strokeDash === 'dash') dashArray = '6 6';
        if (style.strokeDash === 'dot') dashArray = '2 6';
        return {
          color: style.strokeColor,
          strokeColor: style.strokeColor,
          weight: style.strokeWeight,
          strokeWidth: style.strokeWeight,
          opacity: style.strokeOpacity,
          strokeOpacity: style.strokeOpacity,
          fillColor: style.fillColor,
          fillOpacity: style.fillOpacity,
          radius: style.radius,
          dashArray,
        };
      }
      return { color: '#ff7800', weight: 2, opacity: 0.7, fillOpacity: 0.1 };
    },
    [layerStyles],
  );

  // Handler to use map click events inside MapContainer
  const MapClickHandler = () => {
    // Fecha a sidebar se aberta ao clicar no mapa
    const _map = useMapEvents({
      click: () => {
        if (sidebarOpen) setSidebarOpen(false);
      },
    });
    useMapClick();
    return null;
  };

  // Handler para eventos de zoom/move, atualiza estado
  const MapEventsHandler = () => {
    useMapEvents({
      zoomend: (e) => {
        const z = e.target.getZoom();
        // console.log('MapEventsHandler zoomend:', z);
        setZoomLevel(z);
        setBounds(e.target.getBounds());
      },
      moveend: (e) => {
        const z = e.target.getZoom();
        //console.log('MapEventsHandler moveend:', z);
        setZoomLevel(z);
        setBounds(e.target.getBounds());
      },
    });
    return null;
  };

  // Map events logger para debug de zoom/move
  const MapEventsLogger = () => {
    useMapEvents({
      zoomstart: (_e) => {
        /* console.log('MapEventsLogger zoomstart', e.target.getZoom()) */
      },
      zoom: (_e) => {
        /* console.log('MapEventsLogger zoom',      e.target.getZoom()) */
      },
      zoomend: (_e) => {
        /* console.log('MapEventsLogger zoomend',   e.target.getZoom()) */
      },
      moveend: (_e) => {
        /* console.log('MapEventsLogger moveend',   e.target.getZoom()) */
      },
    });
    return null;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '100vw',
        height: 'calc(100vh - 64px)',
        background: '#f7f9fa',
        overflow: 'hidden',
        mt: '64px',
      }}
    >
      {/* Mapa */}
      <Box
        ref={boxRef}
        sx={{
          flex: sidebarOpen ? '0 0 33.33vw' : '1 1 100vw',
          minWidth: 0,
          minHeight: 0,
          height: '100%',
          transition: 'flex-basis 0.3s',
        }}
      >
        <Paper
          elevation={0}
          sx={{ height: '100%', p: 0, overflow: 'hidden', position: 'relative', borderRadius: 0 }}
        >
          <UnifiedSearchBar
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 20,
            }}
          />
          {/* Overlay de processamento enquanto consolidando e aquecendo cache */}
          <ProcessingOverlay
            visible={marker?.processing === true}
            text="Propriedade em processamento"
            processStatus={processStatus}
            onRetry={retryProcessingSearch}
          />
          <MapContainer
            ref={mapRef}
            center={INITIAL_CENTER}
            zoom={INITIAL_ZOOM}
            minZoom={3}
            maxZoom={20}
            style={{ height: '100%', width: '100%' }}
            whenCreated={(mapInstance) => {
              mapRef.current = mapInstance;
              setBounds(mapInstance.getBounds());
            }}
          >
            <ZoomLevelControl />
            <ResetZoomButton />
            <MapEventsHandler />
            <MapEventsLogger />
            <MapClickHandler />
            <LayersControl position="topright">
              <BaseLayersControl />
              <StateBoundariesLayer data={estados} />
              {/* Camada de Municípios: sempre adicionada ao controle, mas só renderiza marcadores em zoom >= threshold */}
              <LayersControl.Overlay name="Municípios" checked>
                {zoomLevel >= 8 && bounds ? (
                  <MunicipalityLayer fetchThreshold={11} bounds={bounds} zoomLevel={zoomLevel} />
                ) : null}
              </LayersControl.Overlay>
              <ThematicLayersControl getStyleForTheme={getStyleForTheme} />
              {/* Camadas Vetoriais (PMTiles) - Biomas, UC, etc. Integradas ao layer-style-config */}
              <VectorTileLayers getStyleForTheme={getStyleForTheme} />
            </LayersControl>
            <LayersControl position="bottomright">
              {wmsLayers.map(({ name, title, baseUrl }) => {
                // Usa sempre o CRS do mapa (normalmente EPSG:3857). Evita conflitos de reprojeção.
                const mapCrs =
                  (mapRef.current && mapRef.current.options && mapRef.current.options.crs) ||
                  L.CRS.EPSG3857;
                return (
                  <LayersControl.Overlay key={`${baseUrl}:${name}`} name={title}>
                    <WMSTileLayer
                      url={baseUrl || 'https://geoservicos.inde.gov.br/geoserver/ICMBio/ows'}
                      layers={name}
                      styles=""
                      format="image/png"
                      transparent
                      // Usa 1.1.1 para evitar problemas de axis-order do 1.3.0
                      version="1.1.1"
                      crs={mapCrs}
                      // Sinaliza servidor para tiles e nomes em maiúsculas (compat GeoServer)
                      tiled
                      uppercase
                      zIndex={600}
                      opacity={0.6}
                    />
                  </LayersControl.Overlay>
                );
              })}
            </LayersControl>
            <FitBoundsHandler themeGeometries={themeGeometries} forceFitBounds={forceFitBounds} />
            {marker && (
              <Marker
                key={`${marker.position[0]},${marker.position[1]}-${showYellow ? 'yellow' : showRed ? 'red' : 'blue'}`}
                position={marker.position}
                icon={showYellow ? yellowIcon : showRed ? redIcon : defaultIcon}
                // Bloqueia clique quando processando (amarelo) ou não encontrado (vermelho)
                eventHandlers={
                  marker.found === false || showYellow
                    ? {}
                    : { click: (e) => handleMarkerClick(e, marker) }
                }
                options={{ markerData: marker }}
              />
            )}
            <LayerStyleConfigButton onOpen={handleOpenLayerStyle} />
          </MapContainer>
          {/* Drawer lateral dentro do Paper, posicionado absolutamente */}
          {open && (
            <div className="absolute top-0 right-0 z-[9999] h-full pointer-events-auto">
              <div
                className="w-full max-w-md bg-white shadow-xl h-full flex flex-col transform transition-transform duration-300"
                style={{
                  backgroundColor: '#ffffff',
                  maxWidth: 420,
                  boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
                }}
              >
                <div className="flex items-center justify-between p-4 border-b bg-white">
                  <h2 className="text-lg font-semibold text-gray-800">Configuração de Camadas</h2>
                  <button
                    onClick={() => setOpen(false)}
                    className="text-gray-600 hover:text-gray-900 text-3xl leading-none font-light"
                    style={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ×
                  </button>
                </div>
                <div className="flex-1 overflow-auto bg-white">
                  <TailwindLayerStyleConfig
                    layerNames={styleConfigLayerNames}
                    styles={pendingStyles}
                    onChange={handlePendingChange}
                  />
                </div>
                <div className="p-4 border-t flex justify-end space-x-2 bg-white">
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded"
                  >
                    Resetar
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          )}
        </Paper>
      </Box>
      {/* PropertyView - Nova interface consolidada em substituição ao Sidebar */}
      <Box
        sx={{
          flex: sidebarOpen ? '0 0 66.66vw' : '0 0 0',
          minWidth: 0,
          height: '100%',
          transition: 'flex-basis 0.3s',
          overflow: 'hidden',
        }}
      >
        <div className="h-full overflow-auto bg-white shadow-lg p-4">
          {sidebarOpen && (
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{sidebarData?.nome || ''}</h2>
              <button
                onClick={() => {
                  //console.log('[SimpleMapView] Fechando PropertyView e resetando contexto');
                  try {
                    // Aplica o reset de contexto ao fechar o PropertyView
                    resetGlobalContext(mapContext, filterContext, false);
                  } catch (resetError) {
                    console.error('[SimpleMapView] Erro ao resetar contextos:', resetError);
                    // Fallback: tenta pelo menos limpar os dados principais
                    if (mapContext.setPropertyData) mapContext.setPropertyData(null);
                    if (mapContext.setGeometries) mapContext.setGeometries([]);
                    if (mapContext.setThemeGeometries) mapContext.setThemeGeometries({});
                  } finally {
                    // Garante que a sidebar será fechada mesmo se houver erro no reset
                    setSidebarOpen(false);
                  }
                }}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {sidebarOpen && sidebarData && (
            <ErrorBoundary showErrorDetails={process.env.NODE_ENV === 'development'}>
              <PropertyView
                propertyData={sidebarData}
                onZoomToGeometry={(geometry) => {
                  if (mapRef.current) {
                    // Função para dar zoom em uma geometria específica
                    // Isso seria implementado mais detalhadamente com base na sua lógica atual
                    try {
                      const bounds = L.geoJSON(geometry).getBounds();
                      mapRef.current.fitBounds(bounds);
                    } catch (error) {
                      console.error('Erro ao dar zoom na geometria:', error);
                    }
                  }
                }}
                onToggleLayerVisibility={(_layerId, _visible) => {
                  // Implementar lógica para mostrar/ocultar camadas
                }}
                onHighlightGeometry={(_geometry) => {
                  // Implementar lógica para destacar uma geometria
                }}
                onApplyStyle={(_layerName, _style) => {
                  // Implementar lógica para aplicar um estilo a uma camada
                }}
              />
            </ErrorBoundary>
          )}

          {sidebarOpen && !sidebarData && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-4">
                <p className="mb-2 text-gray-600">Carregando dados da propriedade...</p>
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            </div>
          )}
        </div>
      </Box>
    </Box>
  );
}

export default SimpleMapView;
